import {Controller, defaultLocationData, LocationFormData} from "../form/controller";
import Form from "../form/Form";
import {FormActionButtons, FormContainer, FormRow} from "@dvrt/graphql-widgets";
import {useDispatch} from "react-redux";
import {showNotification} from "../../../actions";
import {defineMessage} from "react-intl";
import {Page} from "../../pages";
import {useFormGuard} from "../../../utils/formGuard";
import {LocationsQuery, useDomainNameQuery, useLocationsQuery} from "@/queries.generated";
import {ContextFilters, useAuth} from "@/context/AuthProvider";

type Props = {
    locationId: number;
    domainId: number;
};

export default ({locationId, domainId}: Props) => {
    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
    });

    const locationsQuery = useLocationsQuery({
        variables: {
            id: locationId,
        },
    });

    const domainName = domainNameQuery.data?.domains.items[0].name || "";

    const dispatch = useDispatch();
    const auth = useAuth();
    const canEdit = auth.hasPermission("edit_location", ContextFilters.domain(domainName));

    const extraData: Partial<LocationFormData> = {};

    let formData: LocationsQuery["locations"]["items"][number] = null;

    if (locationsQuery.data?.locations.items[0]) {
        formData = locationsQuery.data?.locations.items[0];
        extraData.id = locationId;
    }

    const controller = new Controller((formData as LocationFormData) || defaultLocationData, extraData);
    useFormGuard(controller.isChanged);

    async function submit() {
        const result = await controller.submit();
        if (result) {
            await locationsQuery.refetch();
            dispatch(showNotification(defineMessage({id: "location.updated", defaultMessage: "Location updated"})));
        }
    }

    return (
        <Page
            title={defineMessage({id: "location.settings", defaultMessage: 'Settings for location "{name}"'})}
            titleValues={{name: locationsQuery.data?.locations.items[0].name || ""}}
        >
            <FormContainer style={{maxWidth: 400}}>
                <Form controller={controller} canEdit={canEdit} />
                {canEdit && (
                    <FormRow>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => controller.reset()}
                            enabled={canEdit && controller.isChanged}
                        />
                    </FormRow>
                )}
            </FormContainer>
        </Page>
    );
};
